import React, {useEffect, useState} from 'react';
import {Col, Collapse, Flex, message, Row, Typography} from 'antd';
import Form from '../../components/data_entry/form/Form';
import InputFormField from '../../components/data_entry/form/InputFormField';
import SelectFormField from '../../components/data_entry/form/SelectFormField';
import DatePickerFormField from '../../components/data_entry/form/DatePickerFormField';
import TextAreaFormField from '../../components/data_entry/form/TextAreaFormField';
import CustomButton from '../../components/data_entry/Button';
import ENDPOINTS from '../../constants/endpoints';
import useDefaultRequest from '../../hooks/useDefaultRequest';
import {BuildFieldErrorsDict} from '../../utils/utils';
import api from '../../services/api';
import {useClientAuth} from '../../contexts/ClientAuthContext';

const FORM_NAMES = {
    FIRST_NAME: 'first_name',
    LAST_NAME: 'last_name',
    PHONE: 'phone',
    PROFESSION: 'profession',
    GENDER: 'gender',
    BIRTH_DATE: 'birth_date',
    CPF: 'cpf',
    RG: 'rg',
    DEPENDENT: {
        FIRST_NAME: ['dependent', 'first_name'],
        LAST_NAME: ['dependent', 'last_name'],
        CPF: ['dependent', 'cpf'],
        RG: ['dependent', 'rg'],
        GENDER: ['dependent', 'gender'],
        BIRTH_DATE: ['dependent', 'birth_date'],
        MEDICAL_CONDITION: ['dependent', 'medical_condition'],
    },
};

const GENDER_OPTIONS = [
    {label: 'Masculino', value: 'MALE'},
    {label: 'Feminino', value: 'FEMALE'},
    {label: 'Prefiro não informar', value: 'PREFER_NOT_SAY'},
];

export default function ShopMyData() {
    const [form] = Form.useForm();
    const [submitLoading, setSubmitLoading] = useState(false);
    const {fetchMe} = useClientAuth();

    const {data: profile} = useDefaultRequest({url: ENDPOINTS.AUTH_PROFILE});

    useEffect(() => {
        if (!profile) return;
        form.setFieldsValue({
            [FORM_NAMES.FIRST_NAME]: profile.first_name,
            [FORM_NAMES.LAST_NAME]: profile.last_name,
            [FORM_NAMES.PHONE]: profile.phone,
            [FORM_NAMES.PROFESSION]: profile.profession,
            [FORM_NAMES.GENDER]: profile.gender,
            [FORM_NAMES.BIRTH_DATE]: profile.birth_date,
            [FORM_NAMES.CPF]: profile.cpf,
            [FORM_NAMES.RG]: profile.rg,
            dependent: profile.dependent
                ? {
                    first_name: profile.dependent.first_name,
                    last_name: profile.dependent.last_name,
                    cpf: profile.dependent.cpf,
                    rg: profile.dependent.rg,
                    birth_date: profile.dependent.birth_date,
                    gender: profile.dependent.gender,
                    medical_condition: profile.dependent.medical_condition,
                }
                : undefined,
        });
    }, [profile, form]);

    const onFinish = async (values) => {
        setSubmitLoading(true);
        try {
            const payload = {
                ...values,
            };
            await api.patch(ENDPOINTS.AUTH_PROFILE, payload);
            message.success('Dados atualizados com sucesso');
            fetchMe();
        } catch (error) {
            BuildFieldErrorsDict(error, form.setFields);
        } finally {
            setSubmitLoading(false);
        }
    };

    const showDependent = profile?.dependent?.is_self === false;

    return (
        <>
        <Form form={form} onFinish={onFinish}>
            <Row gutter={[16, 0]}>
                <Col xs={24}>
                    <Typography.Title level={5} style={{marginBottom: 16}}>
                        Dados pessoais
                    </Typography.Title>
                </Col>
                <Col xs={24} md={12}>
                    <InputFormField form={{label: 'Nome', name: FORM_NAMES.FIRST_NAME, rules: [{required: true}]}}/>
                </Col>
                <Col xs={24} md={12}>
                    <InputFormField form={{label: 'Sobrenome', name: FORM_NAMES.LAST_NAME, rules: [{required: true}]}}/>
                </Col>
                <Col xs={24} md={8}>
                    <InputFormField form={{label: 'Telefone', name: FORM_NAMES.PHONE}}/>
                </Col>
                <Col xs={24} md={8}>
                    <InputFormField form={{label: 'Profissão', name: FORM_NAMES.PROFESSION}}/>
                </Col>
                <Col xs={24} md={8}>
                    <SelectFormField
                        options={GENDER_OPTIONS}
                        form={{label: 'Gênero', name: FORM_NAMES.GENDER}}
                    />
                </Col>
                <Col xs={24} md={8}>
                    <DatePickerFormField form={{label: 'Data de Nascimento', name: FORM_NAMES.BIRTH_DATE}}/>
                </Col>
                <Col xs={24} md={8}>
                    <InputFormField form={{label: 'CPF', name: FORM_NAMES.CPF}}/>
                </Col>
                <Col xs={24} md={8}>
                    <InputFormField form={{label: 'RG', name: FORM_NAMES.RG}}/>
                </Col>

                {showDependent && (
                    <Col xs={24} style={{marginTop: 8}}>
                        <Collapse
                            items={[{
                                key: 'dependent',
                                label: 'Dados do dependente',
                                children: (
                                    <Row gutter={[16, 0]}>
                                        <Col xs={24} md={12}>
                                            <InputFormField
                                                form={{label: 'Nome', name: FORM_NAMES.DEPENDENT.FIRST_NAME}}
                                            />
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <InputFormField
                                                form={{label: 'Sobrenome', name: FORM_NAMES.DEPENDENT.LAST_NAME}}
                                            />
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <InputFormField
                                                form={{label: 'CPF', name: FORM_NAMES.DEPENDENT.CPF}}
                                            />
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <InputFormField
                                                form={{label: 'RG', name: FORM_NAMES.DEPENDENT.RG}}
                                            />
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <SelectFormField
                                                options={GENDER_OPTIONS}
                                                form={{label: 'Gênero', name: FORM_NAMES.DEPENDENT.GENDER}}
                                            />
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <DatePickerFormField
                                                form={{label: 'Data de Nascimento', name: FORM_NAMES.DEPENDENT.BIRTH_DATE}}
                                            />
                                        </Col>
                                        <Col xs={24}>
                                            <TextAreaFormField
                                                rows={3}
                                                form={{
                                                    label: 'Condição médica',
                                                    name: FORM_NAMES.DEPENDENT.MEDICAL_CONDITION,
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                ),
                            }]}
                        />
                    </Col>
                )}

                <Col xs={24}>
                    <Flex justify="end" style={{marginTop: 16}}>

                        <CustomButton htmlType="submit" loading={submitLoading}>
                            Salvar alterações
                        </CustomButton>
                    </Flex>
                </Col>
            </Row>
        </Form>


        </>
    );
}
